import { Button, IconButton, TextField, Tooltip } from '@ads/components-react';

import { ArrowLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TARGET_DIMENSIONS } from '../../../constants';
import { useSlideEditorContext } from '../../../context/SlideEditorContext';
import type { ActiveLanguage } from '../../../store/atoms/editorUiAtom';
import type { VariantKey } from '../../../store/atoms/slideAtom';
import { CategorySelector } from '../../molecules/CategorySelector';
import { DeviceToggle } from '../../molecules/DeviceToggle';
import { LanguageSelector } from '../../molecules/LanguageSelector';
import { SaveStatus } from '../../molecules/SaveStatus';

function computeLanguageCompleteness(
  vc: Record<VariantKey, boolean>,
): Partial<Record<ActiveLanguage, boolean>> {
  return {
    'pt-BR': vc['pt-BR-desktop'] || vc['pt-BR-mobile'],
    'en-US': vc['en-US-desktop'] || vc['en-US-mobile'],
    'es-ES': vc['es-ES-desktop'] || vc['es-ES-mobile'],
  };
}

export function EditorTopBar() {
  const { t } = useTranslation('slideEditor');
  const ctx = useSlideEditorContext();
  const navigate = useNavigate();
  const [localTitle, setLocalTitle] = useState(ctx.slideName);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (!isEditingRef.current) setLocalTitle(ctx.slideName);
  }, [ctx.slideName]);

  const handleTitleFocus = () => {
    isEditingRef.current = true;
  };

  const handleTitleBlur = () => {
    isEditingRef.current = false;
    const trimmed = localTitle.trim();
    if (trimmed && trimmed !== ctx.slideName) ctx.setSlideName(trimmed);
    else setLocalTitle(ctx.slideName);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
    if (e.key === 'Escape') {
      isEditingRef.current = false;
      setLocalTitle(ctx.slideName);
      e.currentTarget.blur();
    }
  };

  const canvasSize =
    ctx.activeDevice === 'desktop'
      ? `${TARGET_DIMENSIONS.desktop.width.toString()} × ${TARGET_DIMENSIONS.desktop.height.toString()}`
      : `${TARGET_DIMENSIONS.mobile.width.toString()} × ${TARGET_DIMENSIONS.mobile.height.toString()}`;
  const variantCompleteness = ctx.getVariantCompleteness();
  const langCompleteness = computeLanguageCompleteness(variantCompleteness);
  const canSave = ctx.canSave;

  return (
    <div className="@container flex h-md items-center gap-nano border-b-hairline border-ctx-layout-border bg-ctx-layout-surface px-mini">
      {/* ── Esquerda — shrink-0 para nunca sumir ───── */}
      <div className="flex shrink-0 items-center gap-nano">
        {/* Navegação */}
        <Tooltip title={t('topBar.backToLibrary')} side="bottom">
          <IconButton
            icon={ArrowLeftIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('topBar.backToLibrary')}
            onClick={() => void navigate({ to: '/slide-library' })}
          />
        </Tooltip>

        <div className="h-xs w-[1px] bg-ctx-layout-border" />

        {/* Viewport + tamanho — o badge de tamanho cede primeiro quando a barra fica estreita */}
        <DeviceToggle activeDevice={ctx.activeDevice} onDeviceChange={ctx.setActiveDevice} />
        <span className="hidden shrink-0 rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body px-nano py-quark text-ctx-content-placeholder ts-caption-xxs @[60rem]:inline-block">
          {canvasSize}
        </span>

        <div className="h-xs w-[1px] shrink-0 bg-ctx-layout-border" />

        {/* Idioma */}
        <LanguageSelector
          activeLanguage={ctx.activeLanguage}
          onLanguageChange={lang => void ctx.setActiveLanguage(lang)}
          isTranslating={ctx.isTranslating}
          completeness={langCompleteness}
        />
      </div>

      {/* ── Centro — título (card) + categoria (controle do design system) ── */}
      <div className="flex min-w-none flex-1 items-center justify-center gap-nano px-nano">
        {/* Card do título — clicar em qualquer ponto foca o input; estados de hover/foco sinalizam edição */}
        <TextField
          placeholder={t('topBar.titlePlaceholder')}
          leadingIcon={DocumentTextIcon}
          type="text"
          value={localTitle}
          onChange={e => setLocalTitle(e.target.value)}
          onFocus={handleTitleFocus}
          onBlur={handleTitleBlur}
          onKeyDown={handleTitleKeyDown}
          custom={{
            input: 'max-w-[12rem] @[56rem]:max-w-full',
          }}
          size="md"
        />

        {/* Categoria — cede espaço ao título quando a barra fica estreita */}
        <div className="min-w-none w-[7rem] shrink @[56rem]:w-[12rem]">
          <CategorySelector
            categories={ctx.categories}
            categoryId={ctx.categoryId}
            onCategoryChange={ctx.setCategoryId}
          />
        </div>
      </div>

      {/* ── Direita — shrink-0 para nunca sumir ──── */}
      <div className="flex shrink-0 items-center gap-nano">
        <SaveStatus isSaving={ctx.isSaving} isDirty={ctx.isDirty} />
        {canSave ? (
          <Button
            hierarchy="primary"
            size="md"
            isLoading={ctx.isSaving}
            onClick={ctx.saveToLibrary}
          >
            {t('topBar.save')}
          </Button>
        ) : (
          <Tooltip title={t('topBar.saveBlockedTooltip')} side="bottom">
            <Button hierarchy="primary" size="md" onClick={ctx.saveToLibrary} disabled>
              {t('topBar.save')}
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
